<template>
  <div
    class="mb-3 flex flex-col rounded-sm border p-5"
    :class="{ halfed: isHalf, 'w-full': !isHalf }"
  >
    <div class="flex justify-between" @click="isOpen = !isOpen">
      <div class="flex">
        <div class="flex flex-col">
          <lf-h4 class="mb-4">{{ entry.label }}</lf-h4>
          <span>{{ entry.message }}</span>
        </div>
      </div>

      <div class="inline-flex justify-between">
        <lf-chip
          v-if="entry.status"
          class="cursor-pointer"
          :type="entry.status"
          data-cy="middesk-chip"
        >
          <tooltip @show="() => !isOpen && !!isTooltipVisible">
            <template v-if="!!isTooltipVisible" #content>
              <div v-if="middeskData">
                <p class="mb-2 font-semibold text-gray-500 uppercase">
                  <span>
                    {{ entry.sub_label }}
                    {{ fractals }}
                  </span>
                </p>
                <div
                  v-if="Array.isArray(middeskData) && middeskData?.length"
                  class="space-y-1"
                >
                  <p v-for="(item, i) in middeskData" :key="i">
                    <icon-base
                      width="12"
                      height="12"
                      class="pt-1"
                      :icon="IconBullet"
                      :icon-color="iconColor(item)"
                    ></icon-base>
                    {{ getMiddeskItem(entry, item) }}
                  </p>
                </div>
                <p v-else>
                  <icon-base
                    width="12"
                    height="12"
                    class="pt-1"
                    :icon="IconBullet"
                    :icon-color="
                      !Array.isArray(middeskData) && middeskData
                        ? iconColor(middeskData)
                        : ''
                    "
                  ></icon-base>
                  {{ getMiddeskItem(entry, middeskData) }}
                </p>
              </div>
              <div v-else>
                {{ toolTipRegularText }}
              </div>
            </template>
            <template #default>
              <span
                v-if="
                  entry.key === ENTRIES.WATCHLIST && !Array.isArray(middeskData)
                "
                class="mr-1"
              >
                {{ middeskData?.lists?.length }} Watchlists &bull;
              </span>
              {{ entry.sub_label }}
              {{ entry.status === "success" ? fractals : "" }}
            </template>
          </tooltip>
        </lf-chip>
        <div
          v-if="addressTypes && addressTypes.length < 2"
          class="ml-2 flex h-6-5 w-6-5 cursor-pointer items-center justify-center rounded-full bg-red-100/50 px-1 py-1 text-right text-xs font-semibold whitespace-nowrap text-red-500"
        >
          <icon-base
            v-tooltip="'Not Deliverable'"
            :icon="IconAddressDeliverable"
            icon-color="red"
            opacity="1"
          />
        </div>
        <lf-address-icon
          v-for="addressType in addressTypes"
          :key="addressType.key"
          :address-type="addressType"
          class="ml-2"
        />
        <div class="ml-5">
          <dropdown-chevron :is-open="isOpen" />
        </div>
      </div>
    </div>
    <div
      v-if="isOpen"
      :id="entry.key"
      class="mt-2 border-t px-4 pt-6"
      :class="{
        'h-20': entry.key === ENTRIES.BANKRUPTCIES,
      }"
    >
      <div>
        <slot name="advanced" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { ENTRIES, UTIL_COLORS } from "@/helpers/constants";

import IconAddressDeliverable from "@/components/icons/IconAddressDeliverable.vue";
import IconBullet from "@/components/icons/IconBullet.vue";
import DropdownChevron from "@/components/ui/DropdownChevron.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfAddressIcon from "@/components/ui/LfAddressIcon.vue";
import LfChip from "@/components/ui/LfChip.vue";

import type { Task } from "@/models/commercialData/Middesk";
import type { PropType } from "vue";

const props = defineProps({
  middeskData: {
    type: Object as PropType<Task | Task[]>,
    required: false,
  },
  addressTypes: {
    type: Array as PropType<Task[]>,
    required: false,
  },
  entry: {
    type: Object,
    required: true,
  },
  isHalf: {
    type: Boolean,
    default: true,
  },
  forceOpen: {
    type: Boolean,
    default: false,
  },
  toolTipRegularText: {
    type: String,
    required: false,
  },
});

const isOpen = ref(false);

const getMiddeskItem = (
  entry: Record<string, string | Record<string, string>>,
  item: unknown
) => {
  switch (entry.key) {
    case ENTRIES.ADDRESS_VERIFICATION:
      return (item as Record<string, string>).full_address;
    case ENTRIES.BUSINESS_NAME:
    case ENTRIES.PERSON_VERIFICATION:
    case ENTRIES.TIN:
      return (item as Record<string, string>).name;
  }
};

const iconColor = (
  entry: Task & { submitted?: unknown; verified?: unknown }
) => {
  if (entry?.submitted || entry?.verified) {
    return props.entry.status === "success" ? UTIL_COLORS.SUCCESS : "red";
  }
  return "gray";
};

const isTooltipVisible = computed(
  () =>
    (props.middeskData || props.toolTipRegularText) &&
    props.entry.key !== ENTRIES.WATCHLIST
);

const fractals = computed(() =>
  Array.isArray(props.middeskData) && props.middeskData?.length > 1
    ? `1/${props.middeskData.length}`
    : ""
);

watch(
  () => props.forceOpen,
  (value) => (isOpen.value = value)
);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.halfed {
  width: 49.5%;
}
</style>
